import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, Clock, MapPin } from 'lucide-react';

export default function ContactUs() {
  return (
    <div className="min-h-screen bg-[#050508] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white mb-6 transition-colors text-sm">
          <ArrowLeft size={14} /><span className="font-body">Back to Home</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#007AFF]/15 flex items-center justify-center">
              <Mail className="w-6 h-6 text-[#007AFF]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-white">Contact Us</h1>
              <p className="font-body text-white/40 text-sm">We're here to help</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Contact Information */}
            <div className="space-y-4">
              <div className="glass rounded-2xl p-6">
                <h2 className="font-heading font-semibold text-lg text-white mb-4">Get in Touch</h2>
                <p className="font-body text-white/60 text-sm leading-relaxed mb-6">
                  Have questions about Monk Algo ORB? Need help with your subscription? Our support team is ready to assist you.
                </p>

                <div className="space-y-4">
                  <a href="mailto:support@monkalgo.com" className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#007AFF]/30 transition-colors group">
                    <div className="w-10 h-10 rounded-lg bg-[#007AFF]/15 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#007AFF]" />
                    </div>
                    <div>
                      <div className="font-heading font-medium text-white text-sm group-hover:text-[#007AFF] transition-colors">Email Support</div>
                      <div className="font-body text-white/50 text-xs mt-0.5">support@monkalgo.com</div>
                      <div className="font-body text-white/30 text-xs mt-1">Best for detailed inquiries</div>
                    </div>
                  </a>

                  <a href="https://t.me/MonkAlgoSupport" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#8B5CF6]/30 transition-colors group">
                    <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-5 h-5 text-[#8B5CF6]" />
                    </div>
                    <div>
                      <div className="font-heading font-medium text-white text-sm group-hover:text-[#8B5CF6] transition-colors">Telegram Support</div>
                      <div className="font-body text-white/50 text-xs mt-0.5">@MonkAlgoSupport</div>
                      <div className="font-body text-white/30 text-xs mt-1">Quick responses during business hours</div>
                    </div>
                  </a>
                </div>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-4 h-4 text-[#10B981]" />
                  <h3 className="font-heading font-semibold text-white text-sm">Support Hours</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-body text-white/50 text-xs">Monday - Friday</span>
                    <span className="font-mono text-white/70 text-xs">9:00 AM - 6:00 PM IST</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-body text-white/50 text-xs">Saturday</span>
                    <span className="font-mono text-white/70 text-xs">10:00 AM - 2:00 PM IST</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-body text-white/50 text-xs">Sunday</span>
                    <span className="font-mono text-white/40 text-xs">Closed</span>
                  </div>
                </div>
                <p className="font-body text-white/30 text-xs mt-3">
                  Email responses within 24-48 hours during business days
                </p>
              </div>
            </div>

            {/* FAQ / Common Questions */}
            <div className="glass rounded-2xl p-6">
              <h2 className="font-heading font-semibold text-lg text-white mb-4">Frequently Asked Questions</h2>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-white/3">
                  <h3 className="font-heading font-medium text-white text-sm mb-2">How do I install Monk Algo ORB?</h3>
                  <p className="font-body text-white/50 text-xs leading-relaxed">
                    After subscribing, you receive the MT5 Expert Advisor and/or NinjaTrader 8 strategy files with PDF and video setup guides. Our support team can walk you through the installation process.
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-white/3">
                  <h3 className="font-heading font-medium text-white text-sm mb-2">Which brokers are compatible?</h3>
                  <p className="font-body text-white/50 text-xs leading-relaxed">
                    The MT5 version works with any MT5 broker offering NAS100. The NinjaTrader 8 version runs on MNQ futures with any compatible futures broker.
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-white/3">
                  <h3 className="font-heading font-medium text-white text-sm mb-2">Can I use it on a prop firm account?</h3>
                  <p className="font-body text-white/50 text-xs leading-relaxed">
                    Yes — MT5 for forex/CFD prop firms and NinjaTrader 8 for futures prop firms, with adjustable risk settings. Always check your firm's rules before using automated strategies.
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-white/3">
                  <h3 className="font-heading font-medium text-white text-sm mb-2">How do I cancel my subscription?</h3>
                  <p className="font-body text-white/50 text-xs leading-relaxed">
                    Simply email us at support@monkalgo.com with your cancellation request. Your access will continue until the end of your billing period.
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-white/3">
                  <h3 className="font-heading font-medium text-white text-sm mb-2">Do you offer refunds?</h3>
                  <p className="font-body text-white/50 text-xs leading-relaxed">
                    Please refer to our <Link to="/refund-policy" className="text-[#007AFF] hover:underline">Refund Policy</Link> for detailed information on refund eligibility and process.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="mt-6 glass rounded-2xl p-6">
            <h2 className="font-heading font-semibold text-lg text-white mb-4">Business Information</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-white/40" />
                </div>
                <div>
                  <div className="font-heading font-medium text-white text-sm">Registered Address</div>
                  <div className="font-body text-white/50 text-xs mt-1">India</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-white/40" />
                </div>
                <div>
                  <div className="font-heading font-medium text-white text-sm">Business Email</div>
                  <div className="font-body text-white/50 text-xs mt-1">support@monkalgo.com</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
